import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../auth/context/AuthContext';
import { apiFetch } from '../../../shared/lib/apiClient';

const POLL_INTERVAL = 3000;
const ease = 'cubic-bezier(0.16,1,0.3,1)';
const TERMINAL = new Set(['complete', 'failed', 'needs_triage', 'pending_review']);

// ---------------------------------------------------------------------------
// Document type options — plain language for business owners
// ---------------------------------------------------------------------------

const SPECIFIC_TYPES = [
  { id: 'bill',           label: 'Bill I received',  direction: 'inbound',  document_type: 'invoice',        color: 'bg-amber-50 text-amber-600 border-amber-200', colorActive: 'bg-amber-600 text-white border-amber-600' },
  { id: 'invoice_sent',   label: 'Invoice I sent',   direction: 'outbound', document_type: 'invoice',        color: 'bg-emerald-50 text-emerald-600 border-emerald-200', colorActive: 'bg-emerald-600 text-white border-emerald-600' },
  { id: 'purchase_order', label: 'Purchase order',   direction: 'inbound',  document_type: 'purchase_order', color: 'bg-sky-50 text-sky-600 border-sky-200', colorActive: 'bg-sky-600 text-white border-sky-600' },
  { id: 'credit_memo',    label: 'Credit or refund', direction: 'inbound',  document_type: 'credit_memo',    color: 'bg-rose-50 text-rose-600 border-rose-200', colorActive: 'bg-rose-600 text-white border-rose-600' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const computeFileHash = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

const formatDate = (str) => {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatMoney = (val) => {
  if (val == null) return '';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
};

const STATUS_STYLE = {
  received:    { label: 'Received',   dot: 'bg-zinc-400',    bg: 'bg-zinc-100 text-zinc-600',     bar: 'bg-zinc-400',    spinner: 'border-t-zinc-500' },
  classifying: { label: 'Classifying',dot: 'bg-amber-400',   bg: 'bg-amber-50 text-amber-600',    bar: 'bg-amber-400',   spinner: 'border-t-amber-500' },
  extracting:  { label: 'Extracting', dot: 'bg-blue-400',    bg: 'bg-blue-50 text-blue-600',      bar: 'bg-blue-500',    spinner: 'border-t-blue-500' },
  extracted:   { label: 'Processing', dot: 'bg-teal-400',    bg: 'bg-teal-50 text-teal-600',      bar: 'bg-teal-500',    spinner: 'border-t-teal-500' },
  routing:     { label: 'Processing', dot: 'bg-teal-400',    bg: 'bg-teal-50 text-teal-600',      bar: 'bg-teal-500',    spinner: 'border-t-teal-500' },
  enhancing:   { label: 'Enriching',  dot: 'bg-violet-400',  bg: 'bg-violet-50 text-violet-600',  bar: 'bg-violet-500',  spinner: 'border-t-violet-500' },
  complete:    { label: 'Done',        dot: 'bg-emerald-500', bg: 'bg-emerald-50 text-emerald-700', bar: 'bg-emerald-500', spinner: '' },
  pending_review:{ label: 'New Vendor', dot: 'bg-amber-500',  bg: 'bg-amber-50 text-amber-700',    bar: '',               spinner: '' },
  needs_triage:{ label: 'Needs Input', dot: 'bg-violet-500',  bg: 'bg-violet-50 text-violet-700',  bar: '',               spinner: '' },
  failed:      { label: 'Error',       dot: 'bg-red-400',     bg: 'bg-red-50 text-red-600',        bar: 'bg-red-400',     spinner: '' },
};

// 5-stage pipeline for the progress bar
const PIPELINE_STAGES = ['received', 'classifying', 'extracting', 'routing', 'enriching', 'complete'];

// Map backend statuses to pipeline stage index
const stageIndex = (status) => {
  const map = { received: 0, classifying: 1, extracting: 2, extracted: 3, routing: 3, enhancing: 4, complete: 5 };
  return map[status] ?? -1;
};

const StatusProgress = ({ status }) => {
  const idx = stageIndex(status);
  if (idx === -1) return null;
  const cfg = STATUS_STYLE[status] || STATUS_STYLE.received;
  return (
    <div className="flex items-center gap-0.5 mt-1.5">
      {PIPELINE_STAGES.map((_, i) => (
        <div
          key={i}
          className={`h-[3px] flex-1 rounded-full transition-all duration-700 ${
            i < idx  ? 'bg-zinc-400' :
            i === idx ? (cfg.bar || 'bg-zinc-400') :
                        'bg-zinc-100'
          }`}
        />
      ))}
    </div>
  );
};

const friendlyType = (doc) => {
  if (!doc.document_type && !doc.linked_record_type) return null;
  const t = doc.linked_record_type || doc.document_type;
  const map = {
    bill: 'Bill',
    invoice: doc.direction === 'outbound' ? 'Invoice Sent' : 'Bill',
    credit_memo: 'Credit Memo',
    purchase_order: 'Purchase Order',
    receivable: 'Invoice Sent',
  };
  return map[t] || t?.replace(/_/g, ' ');
};

const TYPE_STYLE = {
  'Bill':           'bg-amber-50 text-amber-600',
  'Credit Memo':    'bg-rose-50 text-rose-600',
  'Purchase Order': 'bg-sky-50 text-sky-600',
  'Invoice Sent':   'bg-emerald-50 text-emerald-600',
};

// ---------------------------------------------------------------------------
// Email forwarding card
// ---------------------------------------------------------------------------

const AP_DOMAIN = 'arbcor.com';

const slugify = (name) =>
  (name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const EmailIngestionCard = ({ company, onUpdated }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState(company?.company?.email_slug || '');
  const [slugInput, setSlugInput] = useState(company?.company?.email_slug || '');
  const [slugStatus, setSlugStatus] = useState(null);
  const [slugSaving, setSlugSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const slugTimerRef = useState(null);
  const fullEmail = slug ? `payables@${slug}.${AP_DOMAIN}` : null;

  useEffect(() => {
    if (!company?.companyId) return;
    (async () => {
      try {
        const res = await apiFetch('/api/ingest-emails', { companyId: company.companyId });
        if (res.ok) {
          const data = await res.json();
          const inv = (data.emails || []).find((e) => e.type === 'invoices');
          if (inv) setEnabled(!!inv.enabled);
        }
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [company?.companyId]);

  const handleToggle = async () => {
    const next = !enabled;
    setSaving(true);
    if (next && !slug) {
      const defaultSlug = slugify(company?.company?.name);
      if (defaultSlug) {
        try {
          const r = await apiFetch('/api/companies', { method: 'PUT', body: JSON.stringify({ email_slug: defaultSlug }), companyId: company.companyId });
          if (r.ok) { setSlug(defaultSlug); setSlugInput(defaultSlug); if (onUpdated) onUpdated(); }
        } catch { /* silent */ }
      }
    }
    try {
      const res = await apiFetch('/api/ingest-emails', { method: 'PUT', body: JSON.stringify({ type: 'invoices', enabled: next }), companyId: company.companyId });
      if (!res.ok) throw new Error();
      setEnabled(next);
    } catch { /* silent */ } finally { setSaving(false); }
  };

  const handleSlugChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlugInput(clean);
    setSlugStatus(null);
    if (slugTimerRef[0]) clearTimeout(slugTimerRef[0]);
    if (!clean || clean === slug) return;
    slugTimerRef[0] = setTimeout(async () => {
      setSlugStatus('checking');
      try {
        const res = await apiFetch(`/api/companies/slug-available?slug=${clean}&companyId=${company.companyId}`);
        const data = await res.json();
        setSlugStatus(data.available ? 'available' : 'taken');
      } catch { setSlugStatus(null); }
    }, 400);
  };

  const handleSaveSlug = async () => {
    if (!slugInput || slugStatus === 'taken') return;
    setSlugSaving(true);
    try {
      const res = await apiFetch('/api/companies', { method: 'PUT', body: JSON.stringify({ email_slug: slugInput }), companyId: company.companyId });
      if (!res.ok) { const data = await res.json(); if (res.status === 409) { setSlugStatus('taken'); return; } throw new Error(data.error); }
      setSlug(slugInput);
      setSlugStatus('saved');
      setTimeout(() => setSlugStatus(null), 2000);
      if (onUpdated) onUpdated();
    } catch { /* silent */ } finally { setSlugSaving(false); }
  };

  const handleCopy = () => { if (!fullEmail) return; navigator.clipboard.writeText(fullEmail); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="bg-white border border-stone-300 rounded-lg p-4 sm:p-5 flex flex-col gap-3 sm:gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-violet-50 flex items-center justify-center shrink-0">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-violet-500 sm:w-4 sm:h-4">
              <rect x="1" y="3" width="14" height="10" rx="1" /><path d="M1 4l7 5 7-5" />
            </svg>
          </div>
          <div>
            <p className="text-[13px] sm:text-sm font-medium text-zinc-900">Forward by Email</p>
            <p className="text-[10px] sm:text-[11px] text-zinc-400 mt-0.5">Get an email address to forward bills and documents to</p>
          </div>
        </div>
        <button onClick={handleToggle} disabled={saving || loading}
          className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${enabled ? 'bg-zinc-900' : 'bg-zinc-200'}`}>
          <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition-transform duration-200 ease-in-out mt-0.5 ${enabled ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'}`} />
        </button>
      </div>

      {enabled && (
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Your Forwarding Address</label>
            <div className="flex items-center">
              <span className="px-2 sm:px-3 py-2 bg-zinc-50 border border-r-0 border-zinc-200 text-xs sm:text-sm text-zinc-400 rounded-l-md whitespace-nowrap">payables@</span>
              <input type="text" value={slugInput} onChange={(e) => handleSlugChange(e.target.value)} placeholder="company-name"
                className="flex-1 px-2 sm:px-3 py-2 border border-zinc-200 text-xs sm:text-sm text-zinc-900 outline-none focus:border-zinc-900 transition-colors duration-300 min-w-0" />
              <span className="px-2 sm:px-3 py-2 bg-zinc-50 border border-l-0 border-zinc-200 text-xs sm:text-sm text-zinc-400 rounded-r-md whitespace-nowrap">.{AP_DOMAIN}</span>
            </div>
            {slugStatus === 'checking' && <p className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1"><span className="w-2 h-2 border border-zinc-300 border-t-zinc-600 rounded-full animate-spin inline-block" />Checking...</p>}
            {slugStatus === 'available' && <p className="text-[10px] text-emerald-600 mt-1">Available</p>}
            {slugStatus === 'taken' && <p className="text-[10px] text-red-500 mt-1">Already taken</p>}
            {slugStatus === 'saved' && <p className="text-[10px] text-emerald-600 mt-1">Saved</p>}
            {slugInput && slugInput !== slug && slugStatus !== 'taken' && (
              <button onClick={handleSaveSlug} disabled={slugSaving || slugStatus === 'checking'}
                className="mt-2 px-3 py-1 bg-zinc-900 text-white text-[10px] uppercase tracking-[0.15em] rounded hover:bg-zinc-800 transition-colors disabled:opacity-50">
                {slugSaving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
          {fullEmail && (
            <div className="flex items-center gap-2 bg-zinc-50 rounded-md px-2.5 sm:px-3 py-2 sm:py-2.5">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400 shrink-0 hidden sm:block" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="14" height="10" rx="1" /><path d="M1 4l7 5 7-5" />
              </svg>
              <span className="text-xs sm:text-sm font-mono text-zinc-700 flex-1 truncate">{fullEmail}</span>
              <button onClick={handleCopy} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors shrink-0">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}
          {!slug && <p className="text-xs text-amber-600">Set an address above to start receiving documents.</p>}
        </div>
      )}

      {!enabled && !loading && (
        <p className="text-xs text-zinc-400 leading-relaxed">
          Turn this on to get a dedicated email address. Forward bills, invoices, POs — we'll handle the rest.
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main InboxView
// ---------------------------------------------------------------------------

const InboxView = ({ loaded }) => {
  const { activeCompany } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  // Delete animation state
  const [dismissingIds, setDismissingIds] = useState(new Set());

  // Vendor grouping — expanded vendors
  const [expandedVendors, setExpandedVendors] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const toggleMonth = (key) => setExpandedMonths((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const toggleVendor = (key) => setExpandedVendors((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // Upload state
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [selectedType, setSelectedType] = useState(null); // null = auto-detect
  const [dragOver, setDragOver] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const fileRef = useRef();
  const pollRef = useRef(null);
  const activeIdsRef = useRef([]);

  // Onboarding overlay
  const [showOnboarding, setShowOnboarding] = useState(null); // null = loading, true/false
  const [onboardingReady, setOnboardingReady] = useState(false);
  const [pipelineStage, setPipelineStage] = useState(-1);
  const [introLine, setIntroLine] = useState(-1);

  // -- Fetch documents --
  const fetchDocuments = useCallback(async () => {
    if (!activeCompany?.companyId) return;
    try {
      const res = await apiFetch('/api/documents', { companyId: activeCompany.companyId });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch { /* silent */ } finally { setLoading(false); }
  }, [activeCompany?.companyId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Reset onboarding state when company changes
  useEffect(() => {
    setShowOnboarding(null);
    setOnboardingReady(false);
  }, [activeCompany?.companyId]);

  // Show onboarding overlay on first visit with no documents (per company)
  useEffect(() => {
    if (loading || showOnboarding !== null) return;
    const companyKey = `arbcor_inbox_onboarded_${activeCompany?.companyId || ''}`;
    const dismissed = localStorage.getItem(companyKey);
    if (dismissed) { setShowOnboarding(false); return; }
    setShowOnboarding(documents.length === 0);
  }, [loading, documents.length, showOnboarding, activeCompany?.companyId]);

  // Trigger intro lines then pipeline
  useEffect(() => {
    if (!showOnboarding) return;
    const timers = [
      setTimeout(() => setOnboardingReady(true), 100),
      setTimeout(() => setIntroLine(0), 400),    // "This is your Inbox."
      setTimeout(() => setIntroLine(1), 1800),   // "Drop any document."
      setTimeout(() => setIntroLine(2), 3200),   // "We handle the rest."
      setTimeout(() => setIntroLine(3), 4800),   // fade out intro, show demo
      setTimeout(() => setPipelineStage(0), 5400),
      setTimeout(() => setPipelineStage(1), 6800),
      setTimeout(() => setPipelineStage(2), 8400),
      setTimeout(() => setPipelineStage(3), 10000),
      setTimeout(() => setPipelineStage(4), 11400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [showOnboarding]);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(`arbcor_inbox_onboarded_${activeCompany?.companyId || ''}`, '1');
  };

  // -- Upload --
  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    const files = Array.from(fileList).filter((f) => f.type === 'application/pdf');
    if (!files.length) return;

    const typeConfig = selectedType ? SPECIFIC_TYPES.find((t) => t.id === selectedType) : null;

    const queueItems = files.map((f, i) => ({
      id: `upload-${Date.now()}-${i}`, fileName: f.name, status: 'uploading', documentId: null, file: f,
    }));
    setUploadQueue((prev) => [...queueItems, ...prev]);

    const concurrency = 5;
    const chunks = [];
    for (let i = 0; i < queueItems.length; i += concurrency) chunks.push(queueItems.slice(i, i + concurrency));

    for (const chunk of chunks) {
      await Promise.all(chunk.map(async (item) => {
        try {
          const fileHash = await computeFileHash(item.file);
          const res = await apiFetch('/api/documents/upload', {
            method: 'POST',
            body: JSON.stringify({
              fileName: item.file.name,
              direction: typeConfig?.direction || null,
              documentType: typeConfig?.document_type || null,
              fileHash,
            }),
          });
          if (res.status === 409) {
            const data = await res.json();
            setUploadQueue((prev) => prev.map((q) => q.id === item.id ? { ...q, status: 'duplicate', errorMessage: data.error } : q));
            return;
          }
          const { uploadUrl, fields, documentId } = await res.json();
          const formData = new FormData();
          Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
          formData.append('file', item.file);
          const s3Res = await fetch(uploadUrl, { method: 'POST', body: formData });
          if (!s3Res.ok) throw new Error();
          setUploadQueue((prev) => prev.map((q) => q.id === item.id ? { ...q, status: 'uploaded', documentId } : q));
        } catch {
          setUploadQueue((prev) => prev.map((q) => q.id === item.id ? { ...q, status: 'error' } : q));
        }
      }));
    }

    await fetchDocuments();
    setTimeout(() => setUploadQueue((prev) => prev.filter((q) => q.status === 'uploading' || q.status === 'duplicate')), 2000);
    setTimeout(() => setUploadQueue((prev) => prev.filter((q) => q.status === 'uploading')), 5000);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); };

  // -- Polling --
  const activeIdsKey = useMemo(
    () => documents.filter((d) => !TERMINAL.has(d.processing_status)).map((d) => d.id).sort().join(','),
    [documents.map((d) => `${d.id}:${d.processing_status}`).join(',')]
  );

  useEffect(() => { activeIdsRef.current = activeIdsKey ? activeIdsKey.split(',') : []; }, [activeIdsKey]);

  useEffect(() => {
    if (!activeIdsKey) { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; return; }
    const poll = async () => {
      const ids = activeIdsRef.current;
      if (!ids.length) return;
      try {
        const res = await apiFetch(`/api/documents/statuses?ids=${ids.join(',')}`);
        const data = await res.json();
        const anyTerminal = (data.statuses || []).some((s) => s.processing_status === 'complete' || s.processing_status === 'pending_review' || s.processing_status === 'needs_triage');
        if (anyTerminal) { fetchDocuments(); return; }
        const map = {};
        (data.statuses || []).forEach((s) => { map[s.id] = s; });
        setDocuments((prev) => prev.map((doc) => {
          const u = map[doc.id];
          return u ? { ...doc, ...u } : doc;
        }));
      } catch { /* silent */ }
    };
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeIdsKey, fetchDocuments]);

  // -- Retry --
  const handleRetry = async (docId) => {
    try {
      await apiFetch(`/api/documents/${docId}/retry`, { method: 'POST' });
      setDocuments((prev) => prev.map((d) => d.id === docId ? { ...d, processing_status: 'received', error_message: null } : d));
    } catch { /* silent */ }
  };

  // -- Force Retry (skip duplicate check) --
  const handleForceRetry = async (docId) => {
    try {
      await apiFetch(`/api/documents/${docId}/force-retry`, { method: 'POST' });
      setDocuments((prev) => prev.map((d) => d.id === docId ? { ...d, processing_status: 'received', error_message: null } : d));
    } catch { /* silent */ }
  };

  // -- Dismiss (delete document) --
  const handleDismiss = async (docId) => {
    setDismissingIds((prev) => new Set(prev).add(docId));
    try {
      await apiFetch(`/api/documents/${docId}`, { method: 'DELETE' });
      // Wait for animation to finish before removing from state
      setTimeout(() => {
        setDocuments((prev) => prev.filter((d) => d.id !== docId));
        setDismissingIds((prev) => { const next = new Set(prev); next.delete(docId); return next; });
      }, 400);
    } catch {
      setDismissingIds((prev) => { const next = new Set(prev); next.delete(docId); return next; });
    }
  };

  // -- Approve vendor (release held documents for enrichment) --
  const handleApproveVendor = async (vendorId) => {
    try {
      const res = await apiFetch(`/api/vendors/${vendorId}/approve`, { method: 'PUT' });
      if (res.ok) {
        // Update all docs for this vendor — they'll start enriching
        setDocuments((prev) => prev.map((d) =>
          d.vendor_id === vendorId
            ? { ...d, processing_status: 'enhancing', needs_review: false, review_reasons: [] }
            : d
        ));
      }
    } catch { /* silent */ }
  };

  // -- Triage --
  const handleTriage = async (docId, action) => {
    try {
      await apiFetch(`/api/documents/${docId}/triage`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      if (action === 'discard') {
        setDocuments((prev) => prev.map((d) => d.id === docId
          ? { ...d, processing_status: 'failed', error_message: 'Discarded — not your document' }
          : d));
      } else {
        setDocuments((prev) => prev.map((d) => d.id === docId
          ? { ...d, processing_status: 'received', direction: action, error_message: null }
          : d));
      }
    } catch { /* silent */ }
  };

  // -- Filter --
  const FILTERS = ['All', 'Processing', 'Done', 'Errors'];
  const filtered = documents.filter((d) => {
    if (filter === 'All') return true;
    if (filter === 'Processing') return !TERMINAL.has(d.processing_status);
    if (filter === 'Done') return d.processing_status === 'complete';
    if (filter === 'Errors') return d.processing_status === 'failed';
    return true;
  });

  const isUploading = uploadQueue.some((q) => q.status === 'uploading');
  const processingCount = documents.filter((d) => !TERMINAL.has(d.processing_status)).length;
  const triageDocs = documents.filter((d) => d.processing_status === 'needs_triage');
  const triageCount = triageDocs.length;
  const reviewCount = documents.filter((d) => d.needs_review && (d.review_reasons || []).includes('new_vendor')).length;

  // -- Group by vendor --
  const extractVendor = (doc) => {
    const match = doc.summary?.match(/^(?:Invoice|Bill|Credit Memo|Purchase Order) from (.+?) to /i);
    return match?.[1] || doc.extracted_data?.header?.vendor_name || null;
  };

  const vendorGroups = useMemo(() => {
    const nonTriage = filtered.filter((d) => d.processing_status !== 'needs_triage');
    const groups = {};
    for (const doc of nonTriage) {
      const vendor = extractVendor(doc) || '__unknown__';
      if (!groups[vendor]) groups[vendor] = [];
      groups[vendor].push(doc);
    }
    // Sort groups: processing first, then by most recent document date
    return Object.entries(groups).sort(([, a], [, b]) => {
      const aProcessing = a.some((d) => !TERMINAL.has(d.processing_status));
      const bProcessing = b.some((d) => !TERMINAL.has(d.processing_status));
      if (aProcessing !== bProcessing) return aProcessing ? -1 : 1;
      const aDate = a[0]?.extracted_data?.header?.document_date || a[0]?.extracted_data?.header?.invoice_date || '';
      const bDate = b[0]?.extracted_data?.header?.document_date || b[0]?.extracted_data?.header?.invoice_date || '';
      return bDate.localeCompare(aDate);
    });
  }, [filtered]);

  return (
    <div className="max-w-[1100px] mx-auto space-y-4 sm:space-y-6 relative">

      {/* -- Header -- */}
      <div
        className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '300ms' }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-light text-zinc-900 mb-0.5 sm:mb-1">Inbox</h1>
            <p className="text-xs sm:text-sm text-zinc-400">Drop your bills, invoices, and POs here — we'll handle the rest</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {reviewCount > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-amber-50 border border-amber-100 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-[11px] sm:text-xs text-amber-600 font-medium">{reviewCount} new vendor{reviewCount !== 1 ? 's' : ''}</span>
              </div>
            )}
            {triageCount > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-violet-50 border border-violet-100 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-violet-500" />
                <span className="text-[11px] sm:text-xs text-violet-600 font-medium">{triageCount} need{triageCount === 1 ? 's' : ''} input</span>
              </div>
            )}
            {processingCount > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-amber-50 border border-amber-100 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-[11px] sm:text-xs text-amber-600 font-medium">{processingCount} processing</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* -- Drop Zone -- */}
      <div
        className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '400ms' }}
      >
        <div
          onClick={() => !isUploading && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`relative overflow-hidden rounded-xl border border-dashed transition-all duration-300 cursor-pointer group ${
            dragOver
              ? 'border-zinc-900 bg-zinc-900 scale-[1.01]'
              : 'border-stone-300/80 bg-white hover:border-stone-400 hover:bg-zinc-50'
          }`}
          style={{ minHeight: '100px' }}
        >
          <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }} />

          <div className="absolute inset-0 opacity-[0.03] pointer-events-none hidden sm:block"
            style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

          <div className="relative flex flex-col items-center justify-center py-6 sm:py-10 px-4 sm:px-8 text-center">
            {isUploading ? (
              <div className="space-y-3 w-full max-w-sm">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
                  <span className="text-sm font-medium text-zinc-700">
                    Uploading {uploadQueue.filter((q) => q.status === 'uploading').length} file{uploadQueue.filter((q) => q.status === 'uploading').length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {uploadQueue.map((q) => (
                    <span key={q.id} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${
                      q.status === 'uploading'  ? 'bg-zinc-100 text-zinc-500' :
                      q.status === 'uploaded'   ? 'bg-emerald-100 text-emerald-700' :
                      q.status === 'duplicate'  ? 'bg-amber-100 text-amber-700' :
                                                  'bg-red-100 text-red-600'
                    }`}>
                      {q.status === 'uploading' && <span className="w-2.5 h-2.5 border border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />}
                      {q.status === 'uploaded'  && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 5l2 2 4-4" /></svg>}
                      {q.status === 'duplicate' && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 1v4M5 7h.01" /></svg>}
                      {q.status === 'error'     && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 2l6 6M8 2l-6 6" /></svg>}
                      <span className="truncate max-w-[140px]">{q.status === 'duplicate' ? 'Duplicate' : q.fileName}</span>
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 sm:mb-3 transition-all duration-300 ${
                  dragOver ? 'bg-white/10 scale-110' : 'bg-zinc-100 group-hover:bg-zinc-200 group-hover:scale-105'
                }`}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                    className={`sm:w-[22px] sm:h-[22px] transition-colors duration-300 ${dragOver ? 'text-white' : 'text-zinc-500'}`}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <path d="M17 8l-5-5-5 5" />
                    <path d="M12 3v12" />
                  </svg>
                </div>
                <p className={`text-sm sm:text-base font-medium mb-0.5 sm:mb-1 transition-colors duration-300 ${dragOver ? 'text-white' : 'text-zinc-800'}`}>
                  {dragOver ? 'Release to upload' : 'Drop your files here'}
                </p>
                <p className={`text-xs sm:text-sm transition-colors duration-300 ${dragOver ? 'text-white/60' : 'text-zinc-400'}`}>
                  or <span className={`underline underline-offset-2 ${dragOver ? 'text-white/80' : 'text-zinc-500 group-hover:text-zinc-700'}`}>browse</span> — PDFs, bulk upload supported
                </p>
              </>
            )}
          </div>
        </div>

        {/* Inline type hint — collapsed by default */}
        <div className="flex items-center justify-center mt-3">
          {!showTypePicker && !selectedType && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowTypePicker(true); }}
              className="text-[11px] text-zinc-400 hover:text-zinc-600 transition-colors"
            >
              I know what these are &rarr;
            </button>
          )}
          {(showTypePicker || selectedType) && (
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <span className="text-[10px] text-zinc-400 uppercase tracking-wider mr-1">These are all:</span>
              {SPECIFIC_TYPES.map((t) => {
                const active = selectedType === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={(e) => { e.stopPropagation(); setSelectedType(active ? null : t.id); }}
                    className={`px-2.5 py-1 rounded-md border text-[11px] font-medium transition-all duration-150 ${
                      active ? t.colorActive : t.color + ' hover:opacity-80'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
              {selectedType && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedType(null); }}
                  className="text-[10px] text-zinc-400 hover:text-zinc-600 ml-1 transition-colors"
                >
                  clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* -- Email Forwarding -- */}
      <div
        className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '450ms' }}
      >
        <EmailIngestionCard company={activeCompany} onUpdated={fetchDocuments} />
      </div>

      {/* -- Needs Input -- */}
      {triageDocs.length > 0 && (
        <div
          className="bg-white border border-stone-300 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '480ms' }}
        >
          <div className="px-4 sm:px-5 py-2.5 sm:py-3 border-b border-violet-100 bg-violet-50/40 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-violet-500 shrink-0">
              <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
            </svg>
            <span className="text-[10px] uppercase tracking-widest text-violet-600 font-medium">
              {triageDocs.length} document{triageDocs.length !== 1 ? 's' : ''} need{triageDocs.length === 1 ? 's' : ''} your input
            </span>
          </div>
          <div className="divide-y divide-violet-50">
            {triageDocs.map((doc) => {
              const classification = doc.classification || {};
              // Support both old (sender_name/recipient_name) and new (party_a/party_b) formats
              const partyA = classification.party_a || classification.sender_name;
              const partyB = classification.party_b || classification.recipient_name;
              const parties = [partyA, partyB].filter(Boolean);
              // Deduplicate
              const uniqueParties = [...new Set(parties.map(p => p.trim()))];

              return (
                <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-5 py-3 hover:bg-violet-50/30 transition-colors">
                  {/* File info */}
                  <div className="min-w-0 sm:shrink-0 sm:w-[220px]">
                    <p className="text-xs text-zinc-700 font-medium truncate" title={doc.file_name}>{doc.file_name}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      {uniqueParties.length === 2
                        ? `${uniqueParties[0]} & ${uniqueParties[1]}`
                        : uniqueParties[0] || 'Unknown parties'}
                    </p>
                  </div>

                  {/* Question + buttons */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 flex-1 min-w-0">
                    <span className="text-[11px] sm:text-xs text-zinc-500 shrink-0">Which one are you?</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {uniqueParties.map((name) => (
                        <button
                          key={name}
                          onClick={() => handleTriage(doc.id, name)}
                          className="px-2.5 sm:px-3 py-1.5 rounded-md text-[11px] font-medium bg-white text-zinc-700 border border-zinc-200 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 transition-all shadow-sm"
                        >
                          {name}
                        </button>
                      ))}
                      <button
                        onClick={() => handleTriage(doc.id, 'discard')}
                        className="px-2.5 sm:px-3 py-1.5 rounded-md text-[11px] text-zinc-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all sm:ml-auto"
                        title="Discard — not my document"
                      >
                        Not mine
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* -- Documents grouped by vendor -- */}
      <div
        className="bg-white border border-stone-300 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '500ms' }}
      >
        {/* Header */}
        <div className="px-4 sm:px-5 py-3 sm:py-3.5 border-b border-zinc-200 flex items-center justify-between gap-2 sm:gap-4">
          <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium shrink-0">
            {(() => { const count = documents.filter(d => d.processing_status !== 'needs_triage').length; return count > 0 ? `${count} Doc${count !== 1 ? 's' : ''}` : 'Documents'; })()}
          </h2>
          <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-2 sm:px-2.5 py-1 text-[10px] uppercase tracking-widest rounded transition-colors duration-150 whitespace-nowrap ${
                  filter === f ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-700'
                }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Outer table header — sender-level overview (desktop only) */}
        <div className="hidden sm:grid grid-cols-[1fr_90px_50px_120px_120px_36px] gap-4 px-5 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-400 font-medium">
          <span>Sender</span>
          <span>Uploaded</span>
          <span className="text-center">Docs</span>
          <span className="text-right">Total</span>
          <span>Status</span>
          <span />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span className="w-4 h-4 border border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : vendorGroups.length === 0 && triageCount === 0 ? (
          /* ── First-time onboarding ── */
          <div className="py-10 px-6">
            <div className="max-w-lg mx-auto">
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-[13px] text-zinc-400">No documents yet</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {vendorGroups.map(([vendorKey, docs]) => {
              const isExpanded = expandedVendors.has(vendorKey);
              const vendorLabel = vendorKey === '__unknown__' ? 'Processing...' : vendorKey;
              const isProcessingGroup = docs.some((d) => !TERMINAL.has(d.processing_status));
              const hasNewVendor = docs.some((d) => d.needs_review && (d.review_reasons || []).includes('new_vendor'));
              const hasPendingReview = docs.some((d) => d.processing_status === 'pending_review');
              const groupVendorId = docs.find((d) => d.vendor_id)?.vendor_id;
              const totalAmount = docs.reduce((sum, d) => {
                const amt = d.extracted_data?.header?.total_amount;
                return sum + (amt ? parseFloat(amt) : 0);
              }, 0);
              const docCount = docs.length;

              // Latest upload date for this vendor
              const latestDate = docs.reduce((latest, d) => {
                const dt = d.created_at || d.extracted_data?.header?.document_date || d.extracted_data?.header?.invoice_date;
                return dt && dt > latest ? dt : latest;
              }, '');
              // Overall status for vendor group
              const hasErrors = docs.some((d) => d.processing_status === 'failed');
              const allDone = docs.every((d) => d.processing_status === 'complete');
              const groupStatus = hasPendingReview
                ? { label: 'Needs Approval', bg: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' }
                : isProcessingGroup
                ? { label: 'Processing', bg: 'bg-amber-50 text-amber-600', dot: 'bg-amber-400' }
                : hasErrors
                ? { label: 'Has Errors', bg: 'bg-red-50 text-red-600', dot: 'bg-red-400' }
                : allDone
                ? { label: 'Done', bg: 'bg-emerald-50 text-emerald-700', dot: '' }
                : { label: 'Mixed', bg: 'bg-zinc-100 text-zinc-600', dot: '' };

              return (
                <div key={vendorKey}>
                  {/* Vendor header row — MOBILE */}
                  <button
                    onClick={() => toggleVendor(vendorKey)}
                    className="sm:hidden w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 active:bg-zinc-50 transition-colors text-left"
                  >
                    <svg
                      width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                      className={`text-zinc-400 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                    >
                      <path d="M3 1l4 4-4 4" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium text-zinc-900 truncate">{vendorLabel}</span>
                        {hasNewVendor && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider font-medium bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
                            New
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-zinc-400">{docCount} doc{docCount !== 1 ? 's' : ''}</span>
                        {totalAmount > 0 && <span className="text-[11px] font-medium text-zinc-700 tabular-nums">{formatMoney(totalAmount)}</span>}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase tracking-wider font-medium shrink-0 ${groupStatus.bg}`}>
                      {isProcessingGroup && <span className={`w-1.5 h-1.5 rounded-full ${groupStatus.dot} animate-pulse`} />}
                      {allDone && (
                        <svg width="8" height="8" viewBox="0 0 9 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 4.5l2 2 4-4" /></svg>
                      )}
                      {groupStatus.label}
                    </span>
                  </button>

                  {/* Vendor header row — DESKTOP */}
                  <button
                    onClick={() => toggleVendor(vendorKey)}
                    className="hidden sm:grid w-full grid-cols-[1fr_90px_50px_120px_120px_36px] gap-4 items-center px-5 py-3 hover:bg-zinc-50 transition-colors text-left"
                  >
                    {/* Sender */}
                    <div className="flex items-center gap-3 min-w-0">
                      <svg
                        width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                        className={`text-zinc-400 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      >
                        <path d="M3 1l4 4-4 4" />
                      </svg>
                      <span className="text-sm font-medium text-zinc-900 truncate">{vendorLabel}</span>
                      {hasNewVendor && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-medium bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
                          New Vendor
                        </span>
                      )}
                    </div>

                    {/* Latest Upload */}
                    <span className="text-[11px] text-zinc-500">{formatDate(latestDate)}</span>

                    {/* Docs count */}
                    <span className="text-xs text-zinc-500 font-medium text-center tabular-nums">{docCount}</span>

                    {/* Total */}
                    <span className="text-sm font-semibold text-zinc-900 tabular-nums text-right">
                      {totalAmount > 0 ? formatMoney(totalAmount) : ''}
                    </span>

                    {/* Group status */}
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium w-fit ${groupStatus.bg}`}>
                      {isProcessingGroup && <span className={`w-1.5 h-1.5 rounded-full ${groupStatus.dot} animate-pulse`} />}
                      {hasErrors && !isProcessingGroup && <span className={`w-1.5 h-1.5 rounded-full ${groupStatus.dot}`} />}
                      {allDone && (
                        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 4.5l2 2 4-4" /></svg>
                      )}
                      {groupStatus.label}
                    </span>

                    <span />
                  </button>

                  {/* Vendor approval bar */}
                  {hasPendingReview && groupVendorId && (
                    <div className="flex items-center justify-end gap-3 px-5 py-1.5 border-t border-zinc-200">
                      <span className="text-[11px] text-zinc-400 mr-auto">New vendor — approve to continue processing</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleApproveVendor(groupVendorId); }}
                        className="px-3 py-1 rounded text-[11px] font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); docs.forEach(d => handleDismiss(d.id)); }}
                        className="px-3 py-1 rounded text-[11px] font-medium text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Reject
                      </button>
                    </div>
                  )}

                  {/* Expanded: document cards */}
                  {isExpanded && (() => {
                    // Group docs by month/year
                    const monthGroups = {};
                    for (const doc of docs) {
                      const rawHdr = doc.extracted_data?.header || {};
                      const dateStr = rawHdr.document_date || rawHdr.invoice_date || doc.created_at;
                      const d = dateStr ? new Date(dateStr) : null;
                      const key = d && !isNaN(d) ? `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}` : '0000-00';
                      const label = d && !isNaN(d) ? d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Unknown Date';
                      if (!monthGroups[key]) monthGroups[key] = { label, docs: [] };
                      monthGroups[key].docs.push(doc);
                    }
                    const sorted = Object.entries(monthGroups).sort(([a], [b]) => b.localeCompare(a));
                    const onlyOneMonth = sorted.length === 1;

                    return (
                    <div className="border-t border-zinc-200">
                      {/* MOBILE rows */}
                      <div className="sm:hidden">
                        {sorted.map(([mKey, { label: monthLabel, docs: monthDocs }]) => {
                          const monthKey = `${vendorKey}::${mKey}`;
                          const isMonthOpen = onlyOneMonth || expandedMonths.has(monthKey);
                          return (
                            <div key={mKey}>
                              {/* Month folder header */}
                              <button
                                onClick={() => !onlyOneMonth && toggleMonth(monthKey)}
                                className={`w-full flex items-center gap-2 px-4 py-2 border-t border-zinc-200/60 bg-zinc-50/80 text-left ${onlyOneMonth ? 'cursor-default' : 'active:bg-zinc-100'}`}
                              >
                                {!onlyOneMonth && (
                                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                    className={`text-zinc-400 shrink-0 transition-transform duration-200 ${isMonthOpen ? 'rotate-90' : ''}`}>
                                    <path d="M3 1l4 4-4 4" />
                                  </svg>
                                )}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400 shrink-0">
                                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                </svg>
                                <span className="text-[11px] font-medium text-zinc-500">{monthLabel}</span>
                                <span className="text-[10px] text-zinc-400">({monthDocs.length})</span>
                              </button>
                              {isMonthOpen && monthDocs.map((doc, idx) => {
                                const isActive = !TERMINAL.has(doc.processing_status);
                                const isFailed = doc.processing_status === 'failed';
                                const isDuplicate = isFailed && doc.error_message?.toLowerCase().includes('duplicate');
                                const rawHdr = doc.extracted_data?.header || {};
                                const hdr = {
                                  ...rawHdr,
                                  document_date: rawHdr.document_date || rawHdr.invoice_date,
                                  document_number: rawHdr.document_number || rawHdr.invoice_number,
                                };
                                const mobileTitle = hdr.document_number ? `#${hdr.document_number}` : doc.file_name;
                                const statusIcon = isActive ? (
                                  <div className="w-[13px] h-[13px] shrink-0">
                                    <div className={`w-full h-full rounded-full border-[1.5px] border-zinc-200 ${(STATUS_STYLE[doc.processing_status] || STATUS_STYLE.received).spinner || 'border-t-zinc-500'} animate-spin`} />
                                  </div>
                                ) : doc.processing_status === 'complete' ? (
                                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500 shrink-0">
                                    <circle cx="6.5" cy="6.5" r="5.5" /><path d="M4 6.5l2 2 3-3" />
                                  </svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-300 shrink-0">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" />
                                  </svg>
                                );

                                return (
                                  <div
                                    key={doc.id}
                                    className={`border-t border-zinc-200/80 px-4 py-2 transition-all duration-300 ${
                                      dismissingIds.has(doc.id)
                                        ? 'bg-red-50 opacity-0 -translate-x-4 overflow-hidden max-h-0 py-0'
                                        : `${idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/80'} opacity-100 translate-x-0`
                                    }`}
                                    style={dismissingIds.has(doc.id) ? { transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)' } : undefined}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="shrink-0">{statusIcon}</div>
                                      <span className="text-xs text-zinc-700 truncate min-w-0 flex-1">{mobileTitle}</span>
                                      {hdr.total_amount && <span className="text-xs font-semibold text-zinc-800 tabular-nums shrink-0">{formatMoney(hdr.total_amount)}</span>}
                                      {isFailed ? (
                                        <>
                                          <button onClick={(e) => { e.stopPropagation(); handleDismiss(doc.id); }}
                                            disabled={dismissingIds.has(doc.id)}
                                            className="w-6 h-6 flex items-center justify-center rounded active:bg-red-100 shrink-0" title="Delete">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-300">
                                              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                            </svg>
                                          </button>
                                          <button onClick={() => isDuplicate ? handleForceRetry(doc.id) : handleRetry(doc.id)}
                                            className="w-6 h-6 flex items-center justify-center rounded active:bg-zinc-200 shrink-0" title={isDuplicate ? 'Process anyway' : 'Retry'}>
                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-red-400">
                                              <path d="M1 6a5 5 0 0 1 9-3M11 6a5 5 0 0 1-9 3" /><path d="M10 1v2.5H7.5M2 11V8.5H4.5" />
                                            </svg>
                                          </button>
                                        </>
                                      ) : (
                                        <button onClick={(e) => { e.stopPropagation(); handleDismiss(doc.id); }}
                                          disabled={dismissingIds.has(doc.id)}
                                          className="w-6 h-6 flex items-center justify-center rounded active:bg-red-100 shrink-0 opacity-0 active:opacity-100" title="Delete">
                                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-300">
                                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                    {isActive && <div className="pl-[21px] mt-1"><StatusProgress status={doc.processing_status} /></div>}
                                    {isFailed && doc.error_message && (
                                      <p className="text-[10px] text-red-400 mt-0.5 pl-[21px] truncate">{doc.error_message}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>

                      {/* DESKTOP — inner header + month-grouped rows with left accent */}
                      <div className="hidden sm:block">
                        {/* Sub-row column header */}
                        <div className="grid grid-cols-[3px_1fr_100px_112px_110px_56px] border-t border-zinc-200 bg-zinc-50/60">
                          <div />
                          <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium py-1.5 pl-4">Document</span>
                          <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium py-1.5 text-right pr-4 border-l border-zinc-200">Amount</span>
                          <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium py-1.5 text-center border-l border-zinc-200">Dates</span>
                          <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium py-1.5 text-center border-l border-zinc-200">Status</span>
                          <div />
                        </div>

                        {sorted.map(([mKey, { label: monthLabel, docs: monthDocs }]) => {
                          const monthKey = `${vendorKey}::${mKey}`;
                          const isMonthOpen = onlyOneMonth || expandedMonths.has(monthKey);
                          return (
                            <div key={mKey}>
                              {/* Month folder header */}
                              <button
                                onClick={() => !onlyOneMonth && toggleMonth(monthKey)}
                                className={`w-full flex items-center gap-2 px-5 py-1.5 border-t border-zinc-200/60 bg-zinc-50/80 text-left ${onlyOneMonth ? 'cursor-default' : 'hover:bg-zinc-100/60 cursor-pointer'}`}
                              >
                                {!onlyOneMonth && (
                                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                    className={`text-zinc-400 shrink-0 transition-transform duration-200 ${isMonthOpen ? 'rotate-90' : ''}`}>
                                    <path d="M3 1l4 4-4 4" />
                                  </svg>
                                )}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400 shrink-0">
                                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                </svg>
                                <span className="text-[10px] font-medium text-zinc-500">{monthLabel}</span>
                                <span className="text-[10px] text-zinc-400">({monthDocs.length})</span>
                              </button>
                              {isMonthOpen && monthDocs.map((doc, idx) => {
                                const status = STATUS_STYLE[doc.processing_status] || STATUS_STYLE.received;
                                const isActive = !TERMINAL.has(doc.processing_status);
                                const isFailed = doc.processing_status === 'failed';
                                const isDuplicate = isFailed && doc.error_message?.toLowerCase().includes('duplicate');
                                const type = friendlyType(doc);
                                const rawHdr = doc.extracted_data?.header || {};
                                const hdr = {
                                  ...rawHdr,
                                  document_date: rawHdr.document_date || rawHdr.invoice_date,
                                  document_number: rawHdr.document_number || rawHdr.invoice_number,
                                };
                                const accentColor = isFailed ? 'bg-red-400' : isActive ? 'bg-amber-400' : 'bg-emerald-400';

                                return (
                                  <div
                                    key={doc.id}
                                    className={`group grid grid-cols-[3px_1fr_100px_112px_110px_56px] border-t transition-all duration-300 ${
                                      dismissingIds.has(doc.id)
                                        ? 'bg-red-50 border-red-100 opacity-0 -translate-x-4 overflow-hidden max-h-0'
                                        : isFailed
                                        ? 'bg-red-50/30 border-zinc-200 hover:bg-red-50/60'
                                        : idx % 2 === 0
                                        ? 'bg-white border-zinc-200 hover:bg-zinc-50/60'
                                        : 'bg-zinc-50/30 border-zinc-200 hover:bg-zinc-50/80'
                                    }`}
                                    style={dismissingIds.has(doc.id) ? { transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)' } : undefined}
                                  >
                                    {/* Left accent bar */}
                                    <div className={`${accentColor} rounded-r`} />

                                    {/* Doc info */}
                                    <div className="min-w-0 py-2 pl-4">
                                      <div className="flex items-center gap-2">
                                        {hdr.document_number ? (
                                          <span className="text-xs text-zinc-800 font-semibold tabular-nums">#{hdr.document_number}</span>
                                        ) : (
                                          <span className="text-xs text-zinc-500 truncate max-w-[180px]">{doc.file_name}</span>
                                        )}
                                        {type && (
                                          <span className={`inline-flex items-center px-1.5 py-px rounded text-[8px] uppercase tracking-wider font-semibold shrink-0 ${TYPE_STYLE[type] || 'bg-zinc-100 text-zinc-500'}`}>
                                            {type}
                                          </span>
                                        )}
                                        {doc.summary && (
                                          <span className="text-[11px] text-zinc-400 truncate max-w-[250px] hidden lg:inline">{doc.summary}</span>
                                        )}
                                      </div>
                                      {isActive && <div className="mt-1 max-w-[160px]"><StatusProgress status={doc.processing_status} /></div>}
                                      {isFailed && doc.error_message && (
                                        <div className="flex items-center gap-2 mt-0.5">
                                          <p className="text-[10px] text-red-400 truncate max-w-[250px]">{doc.error_message}</p>
                                          {isDuplicate && (
                                            <>
                                              <button onClick={(e) => { e.stopPropagation(); handleForceRetry(doc.id); }}
                                                className="text-[9px] font-medium text-zinc-600 hover:text-zinc-900 bg-zinc-100 hover:bg-zinc-200 px-1.5 py-0.5 rounded transition-colors shrink-0">
                                                Process anyway
                                              </button>
                                              <button onClick={(e) => { e.stopPropagation(); handleDismiss(doc.id); }}
                                                disabled={dismissingIds.has(doc.id)}
                                                className="text-[9px] font-medium text-red-400 hover:text-red-600 bg-red-50 hover:bg-red-100 px-1.5 py-0.5 rounded transition-colors shrink-0">
                                                Dismiss
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {/* Amount */}
                                    <span className="text-xs font-semibold text-zinc-800 tabular-nums text-right flex items-center justify-end pr-4 border-l border-zinc-200">
                                      {hdr.total_amount ? formatMoney(hdr.total_amount) : <span className="text-zinc-300">&mdash;</span>}
                                    </span>

                                    {/* Dates */}
                                    <div className="flex items-center justify-center gap-1 border-l border-zinc-200">
                                      {hdr.document_date && <span className="text-[11px] text-zinc-400">{formatDate(hdr.document_date)}</span>}
                                      {hdr.due_date && (
                                        <>
                                          <span className="text-zinc-200 text-[10px]">&rarr;</span>
                                          <span className="text-[11px] text-zinc-500">{formatDate(hdr.due_date)}</span>
                                        </>
                                      )}
                                    </div>

                                    {/* Status */}
                                    <div className="flex items-center justify-center border-l border-zinc-200">
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase tracking-wider font-medium ${status.bg}`}>
                                        {isActive ? (
                                          <span className={`w-1.5 h-1.5 rounded-full ${status.dot} animate-pulse`} />
                                        ) : doc.processing_status === 'complete' ? (
                                          <svg width="8" height="8" viewBox="0 0 9 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 4.5l2 2 4-4" /></svg>
                                        ) : null}
                                        {status.label}
                                      </span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-0.5 justify-end pr-4">
                                      {isFailed && !isDuplicate && (
                                        <button onClick={(e) => { e.stopPropagation(); handleRetry(doc.id); }}
                                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-200 transition-colors opacity-0 group-hover:opacity-100" title="Retry">
                                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-400">
                                            <path d="M1 6a5 5 0 0 1 9-3M11 6a5 5 0 0 1-9 3" /><path d="M10 1v2.5H7.5M2 11V8.5H4.5" />
                                          </svg>
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDismiss(doc.id); }}
                                        disabled={dismissingIds.has(doc.id)}
                                        className={`w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 transition-all ${isFailed ? 'opacity-50 hover:opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                                        title="Delete"
                                      >
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-300 hover:text-red-400 transition-colors">
                                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Full-screen onboarding overlay ── */}
      {showOnboarding && createPortal(
        <div className="fixed top-0 bottom-0 right-0 lg:left-64 left-0 z-[200] bg-white overflow-y-auto">
          {/* Skip */}
          <div className="absolute top-5 right-6 z-10" style={{ opacity: onboardingReady ? 1 : 0, transition: `opacity 0.8s ${ease}`, transitionDelay: '600ms' }}>
            <button onClick={dismissOnboarding} className="text-[12px] text-zinc-300 hover:text-zinc-500 transition-colors">Skip</button>
          </div>

          <div className="flex flex-col items-center justify-center min-h-full px-6 py-16">
            <div className="w-full max-w-[520px]">

              {/* Animated intro lines */}
              <div
                className="text-center relative"
                style={{
                  minHeight: introLine >= 3 ? '0px' : '100px',
                  marginBottom: introLine >= 3 ? '0px' : '40px',
                  opacity: introLine >= 3 ? 0 : 1,
                  transition: `all 0.8s ${ease}`,
                }}
              >
                {[
                  'This is your Inbox.',
                  'Drop any document.',
                  'We handle the rest.',
                ].map((line, i) => (
                  <h1
                    key={i}
                    className="text-[28px] font-light text-zinc-900 absolute inset-x-0 tracking-tight"
                    style={{
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      opacity: introLine === i ? 1 : 0,
                      transform: introLine === i ? 'translateY(0) scale(1)' : introLine > i ? 'translateY(-24px) scale(0.97)' : 'translateY(24px) scale(0.97)',
                      transition: `all 0.7s ${ease}`,
                    }}
                  >
                    {line}
                  </h1>
                ))}
              </div>

              {/* Big title after intro fades */}
              <h2
                className="text-center mb-6 text-[28px] font-light text-zinc-900 tracking-tight"
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  opacity: introLine >= 3 ? 1 : 0,
                  transform: introLine >= 3 ? 'translateY(0)' : 'translateY(12px)',
                  transition: `all 0.8s ${ease}`,
                }}
              >
                Get Started
              </h2>

              {/* ── Animated pipeline visualization ── */}
              <div className="relative">

                {/* Fake document card */}
                <div
                  className="mx-auto w-full max-w-[420px] border border-zinc-200 rounded-lg overflow-hidden bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
                  style={{
                    opacity: pipelineStage >= 0 ? 1 : 0,
                    transform: pipelineStage >= 0 ? 'translateY(0) scale(1)' : 'translateY(-30px) scale(0.95)',
                    transition: `all 0.8s ${ease}`,
                  }}
                >
                  {/* Document header */}
                  <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-red-50 flex items-center justify-center shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-400">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2Z" /><path d="M14 2v6h6" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-zinc-900 truncate">INV-2024-0847.pdf</p>
                      <p className="text-[11px] text-zinc-400">Mesa Metal Works · 2 pages</p>
                    </div>
                    {/* Status badge — animates through states */}
                    <div className="shrink-0">
                      {pipelineStage === 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-zinc-100 text-zinc-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                          Received
                        </span>
                      )}
                      {pipelineStage === 1 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-amber-50 text-amber-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                          Classifying
                        </span>
                      )}
                      {pipelineStage === 2 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-blue-50 text-blue-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                          Extracting
                        </span>
                      )}
                      {pipelineStage === 3 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-violet-50 text-violet-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                          Routing
                        </span>
                      )}
                      {pipelineStage >= 4 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-emerald-50 text-emerald-600">
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2 6l3 3 5-5" /></svg>
                          Done
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-0.5 bg-zinc-100">
                    <div
                      className={`h-full transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
                        pipelineStage >= 4 ? 'bg-emerald-500' : pipelineStage >= 2 ? 'bg-blue-500' : pipelineStage >= 1 ? 'bg-amber-400' : 'bg-zinc-300'
                      }`}
                      style={{ width: pipelineStage < 0 ? '0%' : pipelineStage === 0 ? '5%' : pipelineStage === 1 ? '30%' : pipelineStage === 2 ? '60%' : pipelineStage === 3 ? '85%' : '100%' }}
                    />
                  </div>

                  {/* Extracted data — appears at stage 2+ */}
                  <div
                    className="px-4 py-3 space-y-2 overflow-hidden"
                    style={{
                      maxHeight: pipelineStage >= 2 ? '300px' : '0px',
                      opacity: pipelineStage >= 2 ? 1 : 0,
                      paddingTop: pipelineStage >= 2 ? undefined : 0,
                      paddingBottom: pipelineStage >= 2 ? undefined : 0,
                      transition: `all 0.8s ${ease}`,
                    }}
                  >
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                      {[
                        { label: 'Vendor', value: 'Mesa Metal Works' },
                        { label: 'Date', value: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
                        { label: 'Total', value: '$6,207.00' },
                        { label: 'Terms', value: 'Net 30' },
                      ].map((f) => (
                        <div key={f.label}>
                          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium">{f.label}</p>
                          <p className="text-[13px] text-zinc-900 font-medium">{f.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Line items preview */}
                    <div
                      className="mt-2 border-t border-zinc-200 pt-2"
                      style={{
                        opacity: pipelineStage >= 2 ? 1 : 0,
                        transition: `opacity 0.6s ${ease}`,
                        transitionDelay: '400ms',
                      }}
                    >
                      <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium mb-1.5">Line items</p>
                      <div className="space-y-1">
                        {[
                          { desc: 'Steel plate 4x8 1/4"', qty: '12', amt: '$3,840.00' },
                          { desc: 'Welding rods E7018', qty: '5 boxes', amt: '$425.00' },
                          { desc: 'Cutting & fabrication', qty: '1', amt: '$1,942.00' },
                        ].map((li) => (
                          <div key={li.desc} className="flex items-center gap-2 text-[12px]">
                            <span className="flex-1 text-zinc-600 truncate">{li.desc}</span>
                            <span className="text-zinc-400 tabular-nums shrink-0">{li.qty}</span>
                            <span className="text-zinc-900 font-medium tabular-nums shrink-0 w-20 text-right">{li.amt}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Routed destination — appears at stage 3+ */}
                  <div
                    className="border-t border-zinc-200 overflow-hidden"
                    style={{
                      maxHeight: pipelineStage >= 3 ? '60px' : '0px',
                      opacity: pipelineStage >= 3 ? 1 : 0,
                      transition: `all 0.6s ${ease}`,
                    }}
                  >
                    <div className="px-4 py-2.5 bg-zinc-50 flex items-center gap-2">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-zinc-400">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                      <span className="text-[11px] text-zinc-500">Routed to</span>
                      <span className="text-[11px] font-semibold text-zinc-900 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">Bills</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA — appears after pipeline completes */}
              <div
                className="mt-10 text-center"
                style={{
                  opacity: pipelineStage >= 4 ? 1 : 0,
                  transform: pipelineStage >= 4 ? 'translateY(0)' : 'translateY(12px)',
                  transition: `all 0.7s ${ease}`,
                }}
              >
                <button
                  onClick={dismissOnboarding}
                  className="px-8 py-3 bg-zinc-900 text-white text-[14px] font-medium rounded-md hover:bg-zinc-800 transition-colors"
                >
                  Upload your first document
                </button>
                <p className="text-[12px] text-zinc-400 mt-3">Drag & drop, browse files, or forward via email</p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InboxView;
